/**
 * Status colours come from the database (`status_def.colour`), so they are data an admin can set to
 * anything — including a colour that makes the label unreadable on its own chip.
 * ⇒ We pick the text colour from the background's luminance instead of hard-coding white or black.
 * ⭐ WCAG 2.1 AA is 4.5:1 for body text; choosing the better of black/white per chip is the cheapest
 * thing that keeps an admin's colour choice from silently producing invisible text.
 */

const FALLBACK_BG_LIGHT = '#dfe1e6';
const FALLBACK_BG_DARK = '#454f59';

export interface ChipColours {
  background: string;
  foreground: string;
  border: string;
}

function parseHex(hex: string): [number, number, number] | null {
  const match = /^#?([0-9a-f]{3}|[0-9a-f]{6})$/i.exec(hex.trim());
  if (!match) return null;
  let body = match[1] as string;
  if (body.length === 3) {
    body = body
      .split('')
      .map((ch) => ch + ch)
      .join('');
  }
  const value = Number.parseInt(body, 16);
  return [(value >> 16) & 255, (value >> 8) & 255, value & 255];
}

function channelLuminance(channel: number): number {
  const scaled = channel / 255;
  return scaled <= 0.03928 ? scaled / 12.92 : ((scaled + 0.055) / 1.055) ** 2.4;
}

export function relativeLuminance(rgb: [number, number, number]): number {
  const [r, g, b] = rgb;
  return 0.2126 * channelLuminance(r) + 0.7152 * channelLuminance(g) + 0.0722 * channelLuminance(b);
}

function contrastAgainst(luminance: number, other: number): number {
  const lighter = Math.max(luminance, other);
  const darker = Math.min(luminance, other);
  return (lighter + 0.05) / (darker + 0.05);
}

/** Returns the readable foreground for a given chip background — black or white, whichever wins. */
export function readableForeground(background: string): string {
  const rgb = parseHex(background);
  if (!rgb) return '#172b4d';
  const luminance = relativeLuminance(rgb);
  const onWhite = contrastAgainst(1, luminance);
  const onBlack = contrastAgainst(luminance, 0);
  return onBlack >= onWhite ? '#0b1220' : '#ffffff';
}

export function chipColours(colour: string | null | undefined, isDark: boolean): ChipColours {
  const background = colour && parseHex(colour) ? colour : isDark ? FALLBACK_BG_DARK : FALLBACK_BG_LIGHT;
  const foreground = readableForeground(background);
  return { background, foreground, border: 'transparent' };
}
